<template>
  <div v-if="session" class="text-center">
    <v-dialog
      v-model="dialog"
      width="1700"
      content-class="sessionRecapCard"
      @click:outside="closeDialog"
      @keydown.esc="closeDialog"
    >
      <v-card width="1700">
        <v-card-title class="third white--text font-weight-bold">
          <v-icon size="24" color="white">mdi-calendar-plus</v-icon>
          <span class="ml-2">{{ session.name }} - {{ sessionType }}  </span>
          <v-spacer />
          <v-icon size="28" color="white" class="justify-end" @click="closeDialog">mdi-close</v-icon>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-tabs v-model="tab" vertical>
            <div
              v-for="tabItem in tabs"
              :key="`tab_switcher_session_${tabItem.tabKey}`"
              class="width-270"
            >
              <v-tab v-if="canShowResourceTab(tabItem)" :href="`#${tabItem.tabKey}`" :class="`${tab === tabItem.tabKey ? 'red--text' : 'grey--text'}`" class="mt-4" style="justify-content: start;">
                <v-icon v-if="tabItem.icon" :class="[{'third--text': tab === tabItem.tabKey},'grey--text']">{{ tabItem.icon }}</v-icon>
                <i v-if="tabItem.customIcon" :class="[`icon ${tabItem.customIcon} ${tab === tabItem.tabKey ? 'red--text' : 'grey--text'}`]"></i>
                <span class="ml-2">
                  {{ tabItem.label }}
                </span>
              </v-tab>
            </div>

            <v-tabs-slider color="red" />

            <v-tabs-items v-model="tab" class="no-scroll" style="border-left: solid 1px #c71900;">
              <v-tab-item
                v-for="tabItem in tabs"
                :key="`tab_content_${tabItem.tabKey}`"
                :value="`${tabItem.tabKey}`"
              >
                <v-container>
                  <component :is="tabItem.component" v-if="dialog" :session="session" mode="edit" :close-dialog="closeDialog" class="my-auto" :select-session-date="selectSessionDate" />
                </v-container>
              </v-tab-item>
            </v-tabs-items>
          </v-tabs>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, watch, provide } from '@vue/composition-api';
import { getUnixTime } from 'date-fns';
import { EventBus, repriseSessionType } from '../../helpers/utils';
import { compareSessions, equestrianSession } from '../../helpers/sessions';

import SessionCreateForm from './createForm/SessionCreateForm.vue';
import SessionParticipantHorse from './SessionParticipantHorse.vue';
import SessionInstructorHorse from './SessionInstructorHorse.vue';
import SessionResources from './SessionResources.vue';
import RecurringSessionsList from './RecurringSessionsList.vue';

import store from '@/store';
// import router from '@/router';

export default {
  components: { SessionCreateForm, SessionParticipantHorse, SessionResources, SessionInstructorHorse, RecurringSessionsList },
  setup () {
    const sessions = computed(() => store.getters.dailySessions);
    const selectedDate = computed(() => store.getters.selectedDate);
    const session = ref(null);
    const tab = ref('details');
    const isSessionRecurring = computed(() => session.value && session.value.recurring);
    const selectSessionDate = ref(false);

    const isEquestrianSession = computed(() => equestrianSession(session.value.type));
    const iconToShow = computed(() => isEquestrianSession.value ? 'mdi-horse-human' : 'mdi-account');
    // tabs
    /**
     * @type the tabs array contain either type or notType propriety
     * type means the tab will appear only for the specific type of session
     * not type means the tab will appear for all session except not type types
     */

    const tabs = computed(() => [
      { tabKey: 'details', icon: 'mdi-calendar', label: 'Détails', component: 'SessionCreateForm', type: ['*'] },
      { tabKey: 'sessionParticipants', icon: iconToShow.value, label: 'Participants', component: 'SessionParticipantHorse', type: repriseSessionType },
      { tabKey: 'sessionResources', icon: 'mdi-account', label: 'Ressources', component: 'SessionResources', notType: [...repriseSessionType, ...['FERMETURE']] },
      { tabKey: 'sessionInstructors', icon: iconToShow.value, label: 'Instructeurs', component: 'SessionInstructorHorse', type: repriseSessionType },
      { tabKey: 'recurringSessions', icon: iconToShow.value, label: 'Récurrences', component: 'RecurringSessionsList', type: isSessionRecurring.value ? ['*'] : [] }
    ]);

    const dialog = ref(false);

    const closeDialog = () => {
      dialog.value = false;
      tab.value = 'details';
      session.value = null;
      // router.replace({ query: { } });
    };

    watch(selectedDate, () => {
      if (session.value) {
        session.value.date = getUnixTime(new Date(selectedDate.value));
      }
    });

    watch(sessions, () => {
      if (session.value) {
        const sess = sessions.value.find(s => s.uuid === session.value.uuid);
        if (!sess) {
          closeDialog();
          return;
        }

        const fieldsUpdated = compareSessions(session.value, sess);

        // todo: only update if information change
        if (fieldsUpdated && fieldsUpdated.length) {
          session.value = { ...sess };
          session.value.date = getUnixTime(new Date(selectedDate.value));
          session.value.end_date = sess.end_recurring_date || sess.end_time;
        }
      }
    });

    EventBus.$on('showEditSession', (receivedSession, adicionalFields = {}) => {
      dialog.value = true;
      // router.replace({ query: { id: receivedSession.session.id } });

      session.value = {
        ...receivedSession.session,
        date: receivedSession.session.start_time ? getUnixTime(receivedSession.session.start_time * 1000) : getUnixTime(new Date(selectedDate.value)),
        end_date: receivedSession.session.end_recurring_date || receivedSession.session.end_time
      };
      if(adicionalFields?.selectSessionDate) {
        selectSessionDate.value = true;
      }
    });

    const canShowResourceTab = tabItem => {
      if (tabItem.notType && tabItem.notType.length) {
        return (session.value && session.value.type && !tabItem.notType.includes(session.value.type));
      }

      return (session.value && session.value.type && tabItem.type.includes(session.value.type))
      || (session.value && session.value.type && tabItem.type.includes('*'));
    };

    const sessionType = computed(() => store.getters.referenceValue('equestrian_session_values', session.value.type));

    provide('currentSession', session);

    return { tab, tabs, dialog, closeDialog, session, canShowResourceTab, sessionType, selectSessionDate };
  }
};
</script>
